import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Send, PenLine, FileCheck2, Lock } from 'lucide-react';
import { TESTIDS } from '@/constants/testIds';
import { API_BASE } from '@/lib/api';
import PncLogo from '@/components/PncLogo';
import PncFooter, { LegalStrip } from '@/components/PncFooter';

const STEPS = [
  {
    icon: Send,
    n: '01',
    t: 'Admin sends',
    d: 'Choose a service type, paste recipient emails, and dispatch secure single-use signing links in one click.',
  },
  {
    icon: PenLine,
    n: '02',
    t: 'Contractor signs',
    d: 'A public, login-free page. The contractor fills their details, draws a signature, and submits.',
  },
  {
    icon: FileCheck2,
    n: '03',
    t: 'PDF returns',
    d: 'The completed PDF is generated, stored securely in Firebase, and emailed to PNC HR automatically.',
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen grain">
      <header className="relative z-10 border-b border-[var(--line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-9 w-auto" />
          <Link
            to="/admin"
            data-testid={TESTIDS.homeAdminLink}
            className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-white transition-colors"
          >
            <Lock size={14} strokeWidth={2} /> Admin sign-in
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-20 pb-24">
        <div data-testid={TESTIDS.homeHero} className="text-center max-w-3xl mx-auto">
          <PncLogo size="lg" linkTo={null} />
          <p className="field-label mt-12 mb-5">Contract for Services &middot; E-Signature</p>
          <h1 className="serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Send. Sign. Done.
          </h1>
          <p className="mt-8 text-base sm:text-lg text-[var(--muted)] leading-relaxed max-w-xl mx-auto">
            A focused, low-friction workflow for issuing PNC contractor agreements,
            collecting signatures electronically, and delivering the completed PDF
            back to PNC HR — automatically.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Link
              to="/admin"
              data-testid="home-open-admin-btn"
              className="btn-ink inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold uppercase tracking-[0.08em]"
            >
              Open admin console <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <a
              href={`${API_BASE}/contracts/template`}
              target="_blank"
              rel="noreferrer"
              data-testid="home-view-contract-btn"
              className="btn-ghost inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold uppercase tracking-[0.08em]"
            >
              <FileText size={16} strokeWidth={2} /> View blank contract
            </a>
          </div>
        </div>

        <div className="mt-28 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {STEPS.map(({ icon: Icon, n, t, d }) => (
            <div key={n} data-testid={`home-step-${n}`} className="pnc-card rounded-lg p-8 text-left hover:border-[#3A3A3A] transition-colors">
              <div className="flex items-center justify-between mb-6">
                <Icon size={22} strokeWidth={2} className="text-[var(--silver)]" />
                <span className="serif text-2xl text-[#4A4A4A]">{n}</span>
              </div>
              <p className="field-label mb-2">{t}</p>
              <p className="text-sm text-[var(--muted)] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <div className="mt-24">
          <LegalStrip />
          <p className="text-center text-xs text-[#6B6B6B] mt-4">
            Secured signing links &middot; 30-day expiry &middot; single use
          </p>
        </div>
      </main>

      <PncFooter />
    </div>
  );
}
